import { useCreateWebSocketConnection } from '@modules/oracle/hooks/useCreateWebSocketConnection';
import { OracleSearchActionType } from '@modules/oracle/store/oracleActionType';
import { useOracleSearchStore } from '@modules/oracle/store/oracleSearchStore';
import { WebSocketMessage } from 'generated/graphql';
import { cloneDeep } from 'lodash';
import { useEffect, useRef } from 'react';

export function useCreateOracleSearchSession() {
  const [createWebSocketConnection, { sessionID, webSocket, isLoading }] =
    useCreateWebSocketConnection();
  const dispatch = useOracleSearchStore(({ dispatch }) => dispatch);
  const promptByID = useOracleSearchStore(({ data }) => data.promptByID);
  const userQuoteTokenByResponseID = useOracleSearchStore(
    ({ data }) => data.userQuoteTokenByResponseID,
  );

  const dependencies = {
    promptByID,
    userQuoteTokenByResponseID,
  };
  const dependenciesRef = useRef(dependencies);
  dependenciesRef.current = dependencies;

  useEffect(() => {
    if (!webSocket) {
      return;
    }

    const persistMessage = (data: WebSocketMessage) => {
      if (!data || data.createSession) {
        return;
      }

      // Feedback summary is not part of the promptByID
      if (data.feedbackSummary?.userQuoteToken) {
        const updatedUserQuoteTokenByResponseID = cloneDeep(
          dependenciesRef.current.userQuoteTokenByResponseID,
        );

        updatedUserQuoteTokenByResponseID[data.responseID] = [
          ...(updatedUserQuoteTokenByResponseID[data.responseID] ?? []),
          data.feedbackSummary.userQuoteToken,
        ];

        dispatch({
          type: OracleSearchActionType.SET_DATA,
          payload: {
            userQuoteTokenByResponseID: updatedUserQuoteTokenByResponseID,
          },
        });

        return;
      }

      if (!dependenciesRef.current.promptByID?.[data.responseID]) {
        return;
      }

      // ##TODO, remove this once BE implements the halt response
      if (
        dependenciesRef.current.promptByID?.[data.responseID]?.isResponseHalted
      ) {
        return;
      }

      const updatedPromptByID = cloneDeep(dependenciesRef.current.promptByID);

      const { answer, stepAnswer, steps } = data.assistantResponse ?? {};

      if (data.endOfResponse) {
        updatedPromptByID[data.responseID].endOfResponse = true;
      }

      if (data.error) {
        updatedPromptByID[data.responseID].error = data.error;
      }

      if (answer) {
        const prevAnswerTokensV2 =
          updatedPromptByID[data.responseID].assistantResponse?.answer
            ?.tokensV2;

        answer.tokensV2?.forEach((answerTokenV2) => {
          if (answerTokenV2.artefact) {
            updatedPromptByID[data.responseID].artefactDetailsByTokenID = {
              ...updatedPromptByID[data.responseID].artefactDetailsByTokenID,
              [answerTokenV2.id]: answerTokenV2,
            };
          }

          if (answerTokenV2?.text?.summaryDetails?.examples?.id) {
            const id = answerTokenV2?.text?.summaryDetails.examples?.id;

            updatedPromptByID[data.responseID].feedbackSummaryExampleByID = {
              ...updatedPromptByID[data.responseID].feedbackSummaryExampleByID,
              [id]: answerTokenV2?.text?.summaryDetails.examples,
            };
          }

          if (answerTokenV2.eql) {
            updatedPromptByID[data.responseID].eqlByTokenV2ID = {
              ...updatedPromptByID[data.responseID].eqlByTokenV2ID,
              [answerTokenV2.id]: answerTokenV2.eql,
            };
          }
        });

        answer.userQuote?.forEach((quote) => {
          updatedPromptByID[data.responseID].userQuoteByExampleID = {
            ...updatedPromptByID[data.responseID].userQuoteByExampleID,
            [quote.exampleID]: {
              ...updatedPromptByID[data.responseID]?.userQuoteByExampleID?.[
                quote.exampleID
              ],
              [quote.recordID]: quote,
            },
          };
        });

        updatedPromptByID[data.responseID].assistantResponse = {
          ...updatedPromptByID[data.responseID].assistantResponse,
          answer: {
            ...answer,
            tokensV2: prevAnswerTokensV2
              ? [...prevAnswerTokensV2, ...answer.tokensV2]
              : answer.tokensV2,
          },
        };
      }

      if (steps) {
        updatedPromptByID[data.responseID].assistantResponse = {
          ...updatedPromptByID[data.responseID].assistantResponse,

          steps: [
            ...(updatedPromptByID[data.responseID].assistantResponse?.steps ??
              []),
            ...steps,
          ],
        };
      }

      if (stepAnswer) {
        const prevStepAnswerByID =
          updatedPromptByID[data.responseID].assistantResponse?.stepAnswerByID;

        updatedPromptByID[data.responseID].assistantResponse = {
          ...updatedPromptByID[data.responseID].assistantResponse,
          stepAnswerByID: {
            ...prevStepAnswerByID,
            [stepAnswer.stepID]: {
              ...prevStepAnswerByID?.[stepAnswer.stepID],
              ...stepAnswer,
              tokensV2: [
                ...(prevStepAnswerByID?.[stepAnswer.stepID]?.tokensV2 ?? []),
                ...stepAnswer.tokensV2,
              ],
            },
          },
        };

        stepAnswer.userQuotes?.forEach((userQuote) => {
          updatedPromptByID[data.responseID].userQuoteByExampleID = {
            ...updatedPromptByID[data.responseID].userQuoteByExampleID,
            [userQuote.exampleID]: {
              ...updatedPromptByID[data.responseID]?.userQuoteByExampleID?.[
                userQuote.exampleID
              ],
              [userQuote.recordID]: userQuote,
            },
          };
        });

        stepAnswer.tokensV2?.forEach((stepAnswerTokenV2) => {
          if (stepAnswerTokenV2.artefact) {
            updatedPromptByID[data.responseID].artefactDetailsByTokenID = {
              ...updatedPromptByID[data.responseID].artefactDetailsByTokenID,
              [stepAnswerTokenV2.id]: stepAnswerTokenV2,
            };
          }

          if (stepAnswerTokenV2?.text?.summaryDetails?.examples?.id) {
            const id = stepAnswerTokenV2?.text?.summaryDetails.examples?.id;

            updatedPromptByID[data.responseID].feedbackSummaryExampleByID = {
              ...updatedPromptByID[data.responseID].feedbackSummaryExampleByID,
              [id]: stepAnswerTokenV2?.text?.summaryDetails.examples,
            };
          }

          if (stepAnswerTokenV2.eql) {
            updatedPromptByID[data.responseID].eqlByTokenV2ID = {
              ...updatedPromptByID[data.responseID].eqlByTokenV2ID,
              [stepAnswerTokenV2.id]: stepAnswerTokenV2.eql,
            };
          }
        });
      }

      dispatch({
        type: OracleSearchActionType.SET_DATA,
        payload: {
          promptByID: updatedPromptByID,
        },
      });
    };

    webSocket.addEventListener('message', (event) => {
      if (event.data) {
        const data: WebSocketMessage = JSON.parse(event.data);
        persistMessage(data);
      }
    });
  }, [webSocket, dispatch]);

  useEffect(() => {
    if (sessionID) {
      dispatch({
        type: OracleSearchActionType.SET_DATA,
        payload: { sessionID },
      });
    }
  }, [sessionID, webSocket, dispatch]);

  return { createWebSocketConnection, isLoading };
}
